import {
  type CreateUserTokenRequest,
  type RefreshUserTokenRequest,
  type CreateUserToken,
  type RefreshUserToken,
  CreateUserTokenRequestSchema,
  CreateUserTokenSchema,
  RefreshUserTokenSchema,
} from '@services/Auth/declarations'

import {
  postCreateUserTokenConfig,
  postRefreshTokenConfig,
} from '@services/Auth/api'

import { createRateLimitedRequest, type RequestOptions, watchRequest } from '@services/api/utils'
import { type WatchRequestConfig } from '@services/api/declarations'

import { useApiAuthStore } from '@services/Auth/store'
import { axiosInstance } from '@services/api/apiClient'

// ***
// COMMON
// ***

const serviceName = 'Auth'

// ***
// REQUESTS
// ***

export function useApiAuthStoreRequest() {
  const apiAuthStore = useApiAuthStore()
  const { sendRequest } = apiAuthStore

  // FETCH POST AUTH REFRESH TOKEN
  function fetchPostAuthRefreshToken() {
    delete axiosInstance.defaults.headers.common.Authorization
    createRateLimitedRequest<RefreshUserTokenRequest>({
      sendRequest,
      endpoint: postRefreshTokenConfig,
      requestConfig: {
        headers: {
          Authorization: null,
        },
        skipToken: true,
        withCredentials: true,
      },
      serviceName,
    })
  }

  function watchPostAuthRefreshToken(
    { onSuccess, onError }: WatchRequestConfig<RefreshUserToken>,
  ) {
    const { closeSubscription } = watchRequest<RefreshUserToken>(
      postRefreshTokenConfig,
      {
        onSuccess: (data) => onSuccess?.(RefreshUserTokenSchema.parse(data)),
        onError: (error) => onError?.(error),
      },
    )

    return { closeSubscription }
  }

  // FETCH POST AUTH CREATE USER TOKEN
  function fetchPostAuthCreateUserToken(
    requestConfig: CreateUserTokenRequest,
    options?: RequestOptions,
  ) {
    createRateLimitedRequest<CreateUserTokenRequest>({
      sendRequest,
      endpoint: postCreateUserTokenConfig,
      requestConfig: {
        ...CreateUserTokenRequestSchema.parse(requestConfig),
        headers: {
          Authorization: null,
        },
        withCredentials: true,
        skipToken: true,
      },
      serviceName,
      ...options,
    })
  }

  function watchPostAuthCreateUserToken(
    { onSuccess, onError }: WatchRequestConfig<CreateUserToken>,
  ) {
    const { closeSubscription } = watchRequest<CreateUserToken>(
      postCreateUserTokenConfig,
      {
        onSuccess: (data) => onSuccess?.(CreateUserTokenSchema.parse(data)),
        onError: (error) => onError?.(error),
      },
    )

    return { closeSubscription }
  }

  return {
    store: apiAuthStore,
    fetchPostAuthRefreshToken,
    fetchPostAuthCreateUserToken,
    // watchers
    watchPostAuthRefreshToken,
    watchPostAuthCreateUserToken,
  }
}
